import {
  KEEP_POINTS_VALUE,
  formatCaisseCatalogRewardOfferLine,
  formatCaisseQuoteSavingsLine,
} from '../../utils/caisseFidelite'

/**
 * F2.4b1 / F2.5a3 — Choix catalogue récompenses caisse.
 */
export default function CaisseRecompensePanel({
  solde,
  recompenses,
  loading,
  error,
  selectedRewardId,
  confirmedRewardId,
  quote,
  quoteLoading,
  quoteError,
  notice,
  onKeepPoints,
  onSelectReward,
  onDismissNotice,
  disabled = false,
}) {
  if (loading && (!recompenses || recompenses.length === 0) && !error) {
    return (
      <section className="rounded-xl border border-slate-200 bg-white p-3" aria-busy="true">
        <h4 className="font-semibold text-sm text-slate-900 mb-1">Récompenses</h4>
        <p className="text-xs text-slate-500">Chargement…</p>
      </section>
    )
  }

  if (error) {
    return (
      <section className="rounded-xl border border-amber-200 bg-amber-50 p-3" role="status">
        <h4 className="font-semibold text-sm text-slate-900 mb-1">Récompenses</h4>
        <p className="text-xs text-amber-900">{error}</p>
        <p className="text-xs text-slate-600 mt-1">Vous pouvez encaisser sans récompense.</p>
      </section>
    )
  }

  const keepSelected = !selectedRewardId && !confirmedRewardId
  const activeId = confirmedRewardId || selectedRewardId
  const confirmedReward = confirmedRewardId
    ? (recompenses || []).find((r) => r.rewardId === confirmedRewardId)
    : null
  const savingsLine =
    confirmedRewardId && quote?.applicable && !quoteLoading
      ? formatCaisseQuoteSavingsLine(quote, confirmedReward)
      : null

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-3 space-y-2">
      <div className="flex items-baseline justify-between gap-2">
        <h4 className="font-semibold text-sm text-slate-900">Récompenses</h4>
        <p className="text-xs font-semibold text-emerald-700">{Number(solde) || 0} pts</p>
      </div>

      {notice ? (
        <div
          role="alert"
          className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-950"
        >
          <p>{notice}</p>
          {onDismissNotice ? (
            <button
              type="button"
              className="mt-1 font-semibold text-emerald-800 underline min-h-[40px]"
              onClick={onDismissNotice}
            >
              Fermer
            </button>
          ) : null}
        </div>
      ) : null}

      {(!recompenses || recompenses.length === 0) && (
        <p className="text-xs text-slate-600">
          Aucune récompense échangeable. Les points sont conservés.
        </p>
      )}

      <fieldset disabled={disabled} className="space-y-2">
        <legend className="sr-only">Choisir une récompense ou conserver les points</legend>

        <label
          className={`flex items-start gap-3 p-3 rounded-xl border min-h-[52px] ${
            keepSelected
              ? 'border-emerald-600 bg-emerald-50 ring-2 ring-emerald-600/20'
              : 'border-slate-200 bg-slate-50'
          }`}
        >
          <input
            type="radio"
            name="caisse-recompense"
            className="mt-1 h-5 w-5 accent-emerald-700"
            value={KEEP_POINTS_VALUE}
            checked={keepSelected}
            disabled={disabled}
            onChange={() => onKeepPoints()}
          />
          <span className="text-sm">
            <span className="font-semibold text-slate-900">Conserver les points</span>
            <span className="block text-slate-600 text-xs mt-0.5">
              Aucun point ne sera utilisé (intention vide).
            </span>
          </span>
        </label>

        {(recompenses || []).map((r) => {
          const selected = activeId === r.rewardId
          const locked = !r.accessible
          return (
            <label
              key={r.rewardId}
              className={`flex items-start gap-3 p-3 rounded-xl border min-h-[52px] ${
                selected
                  ? 'border-emerald-600 bg-emerald-50 ring-2 ring-emerald-600/20'
                  : locked
                    ? 'border-slate-100 bg-slate-50 opacity-80'
                    : 'border-slate-200 bg-white'
              }`}
            >
              <input
                type="radio"
                name="caisse-recompense"
                className="mt-1 h-5 w-5 accent-emerald-700"
                value={r.rewardId}
                checked={selected}
                disabled={disabled || locked}
                onChange={() => onSelectReward(r.rewardId)}
              />
              <span className="text-sm flex-1">
                <span className="font-semibold text-slate-900 block">{r.libelle}</span>
                {r.description ? (
                  <span className="block text-xs text-slate-600 mt-0.5">{r.description}</span>
                ) : null}
                <span className="block text-xs text-slate-800 mt-1">
                  {formatCaisseCatalogRewardOfferLine(r)}
                </span>
                {locked ? (
                  <span className="block text-xs font-medium text-amber-800 mt-1">
                    Encore {r.pointsManquants} pts
                  </span>
                ) : (
                  <span className="block text-xs font-medium text-emerald-700 mt-1">Accessible</span>
                )}
              </span>
            </label>
          )
        })}
      </fieldset>

      {quoteLoading ? (
        <p className="text-xs text-slate-500" aria-live="polite">
          Vérification…
        </p>
      ) : null}
      {quoteError ? (
        <p className="text-xs text-red-700" role="alert">
          {quoteError}
        </p>
      ) : null}
      {confirmedRewardId && quote?.applicable && !quoteLoading ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-950">
          <p className="font-semibold">
            {savingsLine ||
              `Intention : ${quote.libelle || 'Récompense'} — −${Number(
                quote.reductionAvantage || 0
              ).toFixed(2)} €`}{' '}
            ({quote.points} pts)
          </p>
          <p className="mt-1 text-slate-700">
            Aucun débit tant que le ticket n’est pas encaissé (estimation quote serveur).
          </p>
        </div>
      ) : null}
    </section>
  )
}
